import CorporationSheetResponseResource from "common/resources/CorporationSheet/CorporationSheetResponseResource";
import ConsultCell from "components/elements/ConsultCell";
import { IPagination } from "components/elements/InfiniteScroll";
import Table from "components/elements/Table";
import { ECellType, IRowProps, TableColumn } from "components/elements/Table/MuiTable";
import I18n from "components/materials/I18n";
import ApplicationConfig from "configs/ApplicationConfig";
import ModuleConfig from "configs/ModuleConfig";
import { useCallback, useEffect, useRef, useState } from "react";
import CorporationService from "services/CorporationService";
import { container } from "tsyringe";

import classes from "./classes.module.scss";

const corporationClientModule = container.resolve(ModuleConfig).get().modules.pages.Clients.props.pages.Corporations.props.pages.CorporationDetails;
const corporationService = container.resolve(CorporationService);
const configService = container.resolve(ApplicationConfig);

export default function Corporations() {
	const [rows, setRows] = useState<IRowProps[]>([]);
	const [count, setCount] = useState<{ count: number }>({ count: 0 });
	const pagination = useRef<IPagination>({ skip: 0, take: configService.get().defaultPagination });
	const [creatorOptions, setCreatorOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [creatorFilter, setCreatorFilter] = useState<string | null>(null);
	const search = useRef<string | null>(null);

	const buildRows = useCallback((clients: CorporationSheetResponseResource[]): IRowProps[] => {
		return clients.map((corporation) => {
			const path = corporationClientModule.props.pages.PersonalInformation.props.path.replace(":corporationId", corporation.corporationId);
			return {
				rowKey: corporation.id,
				raisonSociale: corporation.raisonSociale,
				formeJuridique: corporation.formeJuridique,
				montantTotalSouscriptions: corporation.subscriptionSum,
				dateLastSubscription: corporation.lastSubscriptionDate,
				numberOfProductSubscribed: corporation.numberOfProductSubscribed,
				consult: <ConsultCell to={path} label={I18n.asset.common.consults} />,
			};
		});
	}, []);

	const fetchClients = useCallback(
		async () =>
			corporationService.get(pagination.current, search.current).then((clients) => {
				if (clients.length === 0) return [];

				// Filter by creator if selected
				let filteredData = clients;
				if (creatorFilter) {
					filteredData = clients.filter((corporationSheet) => {
						const creator = corporationSheet.corporation?.creator;
						if (!creator) return false;
						const creatorName = `${creator.firstName} ${creator.lastName}`;
						return creatorName === creatorFilter;
					});
				}

				// Build unique creator list for the select dropdown
				const creators = clients
					.map((item) => item.corporation?.creator)
					.filter((creator): creator is NonNullable<typeof creator> => !!creator)
					.reduce(
						(acc, creator) => {
							const creatorName = `${creator.firstName} ${creator.lastName}`;
							if (!acc.some((c) => c.label === creatorName)) {
								acc.push({ value: creatorName, label: creatorName });
							}
							return acc;
						},
						[] as Array<{ value: string; label: string }>,
					);

				setCreatorOptions((prev) => {
					const combined = [...prev, ...creators];
					const unique = combined.filter((creator, index, self) => index === self.findIndex((c) => c.value === creator.value));
					return unique.sort((a, b) => a.label.localeCompare(b.label));
				});

				setRows((_rows) => [..._rows, ...buildRows(filteredData)]);
				pagination.current.skip += pagination.current.take;
				return filteredData;
			}),
		[buildRows, creatorFilter],
	);

	const onNext = useCallback(
		(release: () => void) => {
			fetchClients().then((clients) => {
				if (!clients.length) return console.warn("No more value to load");
				release();
			});
		},
		[fetchClients],
	);

	useEffect(() => {
		corporationService.count().then(setCount);
	}, []);

	const onSearch = useCallback((searchParam: string, selectedFilter?: string) => {
		pagination.current.skip = 0;
		search.current = (searchParam && searchParam.trim()) || null;
		setCreatorFilter((selectedFilter && selectedFilter.trim()) || null);
		setRows([]);
	}, []);

	return (
		<div className={classes["root"]}>
			{
				<Table
					columnsHead={columsHead}
					rows={rows}
					count={count.count}
					placeholderSearchbar={I18n.trslt(I18n.asset.common.search)}
					onNext={onNext}
					onSearch={onSearch}
					selectOptions={creatorOptions}
					selectPlaceholder="Tous les collaborateurs"
				/>
			}
		</div>
	);
}

const columsHead: readonly TableColumn[] = [
	{
		headContent: I18n.asset.pages.corporations.table.columns.raisonSociale,
		key: "raisonSociale",
	},
	{
		headContent: I18n.asset.pages.corporations.table.columns.formeJuridique,
		key: "formeJuridique",
	},
	{
		headContent: I18n.asset.pages.clients.table.columns.number_of_subscriptions,
		key: "numberOfProductSubscribed",
	},
	{
		headContent: I18n.asset.pages.corporations.table.columns.montantTotalSouscriptions,
		key: "montantTotalSouscriptions",
		formatType: ECellType.EURO,
	},
	{
		headContent: I18n.asset.pages.corporations.table.columns.dateLastSubscription,
		key: "dateLastSubscription",
		formatType: ECellType.DATE,
	},
	{
		headContent: "",
		key: "consult",
	},
];
